/**
 * Phase 3 — StudyValidationPanel
 *
 * Calls /api/optimisation/validate on debounce as the composer
 * fields change. Renders blocking errors (red) + warnings (amber) +
 * the model-credibility summary + the estimated counts.
 */
import { useEffect, useState } from "react";
import { AlertCircle, AlertTriangle, ShieldCheck } from "lucide-react";
import optimisation from "@/api/optimisation";
import { formatApiError } from "@/lib/formatApiError";

export default function StudyValidationPanel({
    body,
    onResult,
    debounceMs = 500,
}) {
    const [result, setResult] = useState(null);
    const [validating, setValidating] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        if (!body) return undefined;
        // Skip if nothing meaningful to validate
        if (!body.baseline?.scenario_id || !(body.variables || []).length) {
            setResult(null);
            if (onResult) onResult(null);
            return undefined;
        }
        const handle = setTimeout(async () => {
            setValidating(true);
            setErr(null);
            try {
                const r = await optimisation.validateStudy(body);
                setResult(r);
                if (onResult) onResult(r);
            } catch (e) {
                // Phase 9 Track A — coerce the axios error to a string
                // BEFORE it lands in React state. A Pydantic v2 422
                // returns ``detail: [{type, loc, msg, input, url}, ...]``
                // which previously rendered as a JSX child and tripped
                // React error #31 ("Objects are not valid as a React
                // child"). ``formatApiError`` always returns a string.
                setErr(formatApiError(e, "Validation failed"));
                setResult(null);
                if (onResult) onResult(null);
            } finally {
                setValidating(false);
            }
        }, debounceMs);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(body || {}), debounceMs]);

    if (!body?.baseline?.scenario_id) {
        return (
            <div
                className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-3 text-[12px] text-ux-text-3"
                data-testid="validation-panel-empty"
            >
                Pick a baseline scenario to start validation.
            </div>
        );
    }

    if (err) {
        return (
            <div
                className="rounded-md border border-ux-danger/40 bg-ux-danger-soft/40 p-3 text-[12px] text-ux-danger"
                data-testid="validation-panel-error"
            >
                {err}
            </div>
        );
    }

    if (!result) {
        return (
            <div
                className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-3 text-[12px] text-ux-text-3"
                data-testid="validation-panel-loading"
            >
                {validating ? "Validating…" : "Pick variables to estimate this study."}
            </div>
        );
    }

    const errors = result.errors || [];
    const warnings = result.warnings || [];
    const ok = result.valid && errors.length === 0;

    return (
        <div
            className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-3 space-y-3"
            data-testid="validation-panel"
        >
            <div className="flex items-center gap-2">
                {ok ? (
                    <ShieldCheck className="w-4 h-4 text-ux-success" />
                ) : (
                    <AlertCircle className="w-4 h-4 text-ux-danger" />
                )}
                <span
                    className={[
                        "text-[12px] font-medium",
                        ok ? "text-ux-success" : "text-ux-danger",
                    ].join(" ")}
                    data-testid="validation-panel-status"
                >
                    {ok ? "Ready to run" : `${errors.length} blocking issue${errors.length === 1 ? "" : "s"}`}
                </span>
                {validating && (
                    <span className="text-[10px] text-ux-text-3 italic">
                        re-validating…
                    </span>
                )}
            </div>

            {errors.length > 0 && (
                <ul className="space-y-1" data-testid="validation-panel-errors">
                    {errors.map((e, idx) => (
                        <li
                            key={idx}
                            className="text-[12px] text-ux-danger flex items-start gap-2"
                        >
                            <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <span>
                                {e.path ? <code className="font-mono">{e.path}</code> : null}
                                {e.path ? " — " : ""}
                                {e.message}
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            {warnings.length > 0 && (
                <ul className="space-y-1" data-testid="validation-panel-warnings">
                    {warnings.map((w, idx) => (
                        <li
                            key={idx}
                            className="text-[12px] text-ux-warning flex items-start gap-2"
                        >
                            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <span>
                                {w.path ? <code className="font-mono">{w.path}</code> : null}
                                {w.path ? " — " : ""}
                                {w.message}
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            {result.baseline_already_meets_target && (
                <div className="rounded border border-ux-info/40 bg-ux-info-soft/40 p-2 text-[12px] text-ux-info">
                    Baseline already meets the target Pk. Running the study will
                    short-circuit immediately.
                </div>
            )}

            <div className="grid grid-cols-3 gap-2 text-center pt-2 border-t border-ux-border-1">
                <Stat
                    value={result.estimated_candidates ?? 0}
                    label="candidates"
                    testId="validation-panel-est-candidates"
                />
                <Stat
                    value={result.estimated_baseline_runs ?? 0}
                    label="baseline runs"
                    testId="validation-panel-est-baseline"
                />
                <Stat
                    value={result.estimated_confirmation_runs ?? 0}
                    label="confirmation runs"
                    testId="validation-panel-est-confirmation"
                />
            </div>

            {result.model_confidence_summary && (
                <ConfidenceSummary summary={result.model_confidence_summary} />
            )}
        </div>
    );
}

function Stat({ value, label, testId }) {
    return (
        <div data-testid={testId}>
            <div className="text-[16px] tabular-nums text-ux-text-1 font-medium">
                {value}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-ux-text-3">
                {label}
            </div>
        </div>
    );
}

function ConfidenceSummary({ summary }) {
    if (!summary || typeof summary !== "object") return null;
    const tiers = summary.tiers || {};
    const counts = Object.entries(tiers).filter(([, v]) => typeof v === "number");
    if (counts.length === 0) return null;
    return (
        <div
            className="text-[11px] text-ux-text-3 space-y-1 pt-2 border-t border-ux-border-1"
            data-testid="validation-panel-confidence"
        >
            <div className="uppercase tracking-wider">Model credibility</div>
            <div className="flex gap-3 flex-wrap">
                {counts.map(([tier, n]) => (
                    <span key={tier}>
                        <span className="text-ux-text-2 tabular-nums">{n}</span> {tier}
                    </span>
                ))}
            </div>
            {summary.message && (
                <p className="text-ux-text-3 italic">{summary.message}</p>
            )}
        </div>
    );
}
